import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { openAuthenticatedFile } from '../../api/client.js';
import type { Quote } from '../../api/types.js';
import { QuoteView } from '../../components/QuoteView.js';
import { Alert, PageHeader, datum, fehlertext, useLoad } from '../shared/ui.js';

/**
 * Archivansicht eines Angebots fuer Betreiber und Vertrieb.
 *
 * **Nur lesen.** Es gibt hier bewusst keinen Knopf zum Bearbeiten, Neuberechnen,
 * Loeschen oder Revidieren -- auch nicht fuer Entwuerfe. Ausgestellte Angebote sind
 * archivierte Belege; eine neue Fassung entsteht, wenn ueberhaupt, im Kundenkontext
 * ueber den bestehenden Workflow, nicht aus dem Archiv heraus.
 *
 * Liest `GET /quotes/:id`, das fuer interne Rollen kontouebergreifend antwortet;
 * eine zweite Detailroute gibt es nicht. Die Darstellung ist dieselbe `QuoteView`
 * wie im Kundenworkflow.
 */
export function QuoteDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { data: quote, error } = useLoad<Quote>(
    id === undefined ? null : `/api/quotes/${id}`,
  );
  // Kein Konto beim Schnellangebot -- dann gibt es nichts nachzuladen.
  const konto = useLoad<{ name: string; accountNumber: string }>(
    quote === null || quote.accountId === null
      ? null
      : `/api/admin/accounts/${quote.accountId}`,
  );
  const [fehler, setFehler] = useState<string | null>(null);

  return (
    <>
      <PageHeader
        title={
          quote === null
            ? 'Angebot'
            : quote.quoteNumber === null
              ? 'Angebotsentwurf'
              : `Angebot ${quote.quoteNumber}`
        }
        lead="Archivansicht. Ausgestellte Angebote sind unveraenderlich; hier wird nur gelesen."
        actions={
          <>
            <Link to="/admin/angebote" className="button">
              Zur Uebersicht
            </Link>
            {quote !== null && quote.pdfAvailable && (
              <button
                type="button"
                className="primary"
                onClick={() => {
                  setFehler(null);
                  void openAuthenticatedFile(`/api/quotes/${quote.quoteId}/pdf`).catch(
                    (e: unknown) => setFehler(fehlertext(e)),
                  );
                }}
              >
                PDF oeffnen
              </button>
            )}
          </>
        }
      />
      <Alert text={error} />
      <Alert text={fehler} />
      {quote !== null && (
        <section className="card">
          <dl className="kopfdaten">
            <dt>Kunde</dt>
            <dd>
              {quote.recipientName !== null ? (
                <>
                  <strong>{quote.recipientName}</strong>
                  {quote.recipientNumber !== null && ` (${quote.recipientNumber})`}
                  <div className="note">Schnellangebot -- kein Konto.</div>
                </>
              ) : konto.data === null ? (
                <span className="muted">wird geladen …</span>
              ) : (
                <Link to={`/admin/kunden/${quote.accountId}`}>
                  {konto.data.name} ({konto.data.accountNumber})
                </Link>
              )}
            </dd>
            <dt>Angebotsnummer</dt>
            <dd>
              {quote.quoteNumber === null ? (
                <span className="muted">noch keine (Entwurf)</span>
              ) : (
                <code>{quote.quoteNumber}</code>
              )}
            </dd>
            <dt>Ausgestellt</dt>
            <dd>
              {quote.issuedAt === null ? (
                <span className="muted">–</span>
              ) : (
                datum(quote.issuedAt)
              )}
            </dd>
            <dt>Gueltig bis</dt>
            <dd>{datum(quote.validUntil)}</dd>
            <dt>Waehrung</dt>
            <dd>{quote.currency}</dd>
            <dt>PDF</dt>
            <dd>
              {quote.pdfAvailable ? (
                'archiviert'
              ) : (
                <span className="muted">kein PDF -- Entwurf, noch nicht ausgestellt</span>
              )}
            </dd>
          </dl>
          <QuoteView quote={quote} mitBezeichnung />
        </section>
      )}
    </>
  );
}
